import React from 'react';
import { Button, Select } from 'antd';
import { Colors } from '@common/tokens';
import {
  CurrencyInput,
  Icon,
  InfoTooltip,
  LoadingState,
  ZonedTime,
  Table,
  Text,
} from '@common/components';
import {
  MatchBreakdownType,
  MATCH_BREAKDOWN,
  MatchBreakdownQueryResponse,
} from '@scheduling/graphql';
import { useQuery } from '@common/hooks';
import { IncentiveRateDisplay } from '@scheduling/components';
import {
  formatTierOptionLabel,
  formatOverrideRateOptionLabel,
  formatBaseRateOptionLabel,
  formatCurrentRateLabel,
} from '@scheduling/services';

export function MatchBreakdownDrawerTable({
  unitId,
  shiftId,
  matchBreakdownEditMode,
  setMatchBreakdownEditMode,
  wfgOverrideRates,
  setWfgOverrideRates,
  shiftEditMode,
  canEditRates,
}: {
  unitId: string;
  shiftId: string;
  matchBreakdownEditMode: boolean;
  setMatchBreakdownEditMode: (value: boolean) => void;
  wfgOverrideRates: {
    workforceGroupId: string;
    rate: number;
    moneyAmountCents: number | null;
  }[];
  setWfgOverrideRates: (
    value: {
      workforceGroupId: string;
      rate: number;
      moneyAmountCents: number | null;
    }[]
  ) => void;
  shiftEditMode: boolean;
  canEditRates: boolean;
}) {
  const { data, loading } = useQuery<MatchBreakdownQueryResponse>(
    MATCH_BREAKDOWN,
    {
      variables: { organizationUnitId: unitId, shiftGapId: shiftId },
    }
  );

  const shiftHasMatches = (data?.workforceGroupMatchBreakdown?.length || 0) > 0;
  const anyRowHasOverrideOptions = (
    data?.workforceGroupMatchBreakdown || []
  ).some((row) => row.overrideRateOptions.length > 0);
  const anyRowCanEditRate = (data?.workforceGroupMatchBreakdown || []).some(
    (row) => row.canEditRate
  );
  const usesCompactRateColumn = anyRowHasOverrideOptions;

  if (loading) {
    return <LoadingState />;
  }

  const columns = [
    {
      title: 'Group',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return record.workforceGroupName;
      },
    },
    {
      title: 'Available Staff',
      dataIndex: 'rate',
      key: 'rate',
      width: usesCompactRateColumn ? '15%' : '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return record.availableResources;
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Rate
          {canEditRates &&
            !shiftEditMode &&
            shiftHasMatches &&
            anyRowCanEditRate && (
              <Button
                type="link"
                onClick={() =>
                  setMatchBreakdownEditMode(!matchBreakdownEditMode)
                }
                style={{
                  height: 22,
                  paddingTop: 0,
                  paddingBottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Icon icon="pen" kind="solid" size="sm" />
              </Button>
            )}
        </div>
      ),
      dataIndex: 'avg_rate',
      key: 'avg_rate',
      width: usesCompactRateColumn ? '35%' : '25%',
      render: (id: string, record: MatchBreakdownType) => {
        // NOTE: the server validates the whole (rate, moneyAmountCents) tuple
        // against the offered options, so both have to travel together.
        const handleRateChange = (
          rateCents: number,
          moneyAmountCents: number | null = null
        ) => {
          const edition = {
            workforceGroupId: record.id,
            rate: rateCents,
            moneyAmountCents,
          };
          const editedWfg = wfgOverrideRates.find(
            (wfg) => wfg.workforceGroupId === record.id
          );
          if (!editedWfg) {
            setWfgOverrideRates(wfgOverrideRates.concat([edition]));
          } else {
            setWfgOverrideRates(
              wfgOverrideRates.map((wfg) =>
                record.id === wfg.workforceGroupId ? edition : wfg
              )
            );
          }
        };

        const renderReadOnlyRate = () => (
          <IncentiveRateDisplay
            displayAsLumpSum={record.displayAsLumpSum}
            formattedLumpSumRate={record.formattedLumpSumRate}
            formattedPerHourRate={(record.averageRate / 100).toLocaleString(
              'en-US',
              { style: 'currency', currency: 'USD' }
            )}
            tierLabel={record.incentiveTierLabel}
          />
        );

        if (!matchBreakdownEditMode || !record.canEditRate) {
          return renderReadOnlyRate();
        }

        // NOTE: reverting reads baseRate; averageRate is the persisted override
        // when one exists, so reverting to that would re-apply it.
        const revertRateCents = record.baseRate;

        if (record.overrideRateOptions.length > 0) {
          const sortedOverrideOptions = [...record.overrideRateOptions].sort(
            (a, b) => a.incentiveCents - b.incentiveCents
          );
          const encodeOption = (
            incentiveCents: number,
            moneyAmountCents: number | null
          ) => `${incentiveCents}:${moneyAmountCents ?? ''}`;
          const decodeOption = (value: string) => {
            const [incentiveStr, moneyStr] = value.split(':');
            return {
              incentiveCents: Number(incentiveStr),
              moneyAmountCents: moneyStr === '' ? null : Number(moneyStr),
            };
          };
          return (
            <Select
              labelInValue
              defaultValue={{
                value: encodeOption(record.averageRate, null),
                label: formatCurrentRateLabel(record),
              }}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              onChange={(option) => {
                const { incentiveCents, moneyAmountCents } = decodeOption(
                  String(option.value)
                );
                handleRateChange(incentiveCents, moneyAmountCents);
              }}
              placeholder="Select incentive"
            >
              <Select.Option
                key="base-rate"
                value={encodeOption(revertRateCents, null)}
              >
                {formatBaseRateOptionLabel(
                  revertRateCents,
                  record.displayAsLumpSum,
                  record.shiftDuration
                )}
              </Select.Option>
              {sortedOverrideOptions.map((option, index) => (
                <Select.Option
                  key={`${option.displayName}-${option.incentiveCents}-${option.moneyAmountCents}-${index}`}
                  value={encodeOption(
                    option.incentiveCents,
                    option.moneyAmountCents
                  )}
                >
                  {formatOverrideRateOptionLabel(
                    option,
                    record.displayAsLumpSum,
                    record.shiftDuration
                  )}
                </Select.Option>
              ))}
            </Select>
          );
        }

        if (record.staticPricingTiers.length > 0) {
          const sortedTiers = [...record.staticPricingTiers].sort(
            (a, b) => a.payRate - b.payRate
          );
          return (
            <Select
              labelInValue
              defaultValue={{
                value: record.averageRate,
                label: formatCurrentRateLabel(record),
              }}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              onChange={(option) =>
                handleRateChange(Number(option.value), null)
              }
            >
              <Select.Option key="base-rate" value={revertRateCents}>
                {formatBaseRateOptionLabel(
                  revertRateCents,
                  record.displayAsLumpSum,
                  record.shiftDuration
                )}
              </Select.Option>
              {sortedTiers.map((tier) => (
                <Select.Option
                  key={tier.id}
                  value={Math.round(tier.payRate * 100)}
                >
                  {formatTierOptionLabel(
                    tier,
                    record.displayAsLumpSum,
                    record.shiftDuration
                  )}
                </Select.Option>
              ))}
            </Select>
          );
        }

        return (
          <CurrencyInput
            autoFocus={true}
            style={{ width: 90, textAlign: 'center' }}
            placeholder={(record.averageRate / 100).toString()}
            defaultValue={record.averageRate / 100}
            onChange={(value) => {
              if (typeof value !== 'number') return;
              handleRateChange(Math.round(value * 100));
            }}
          />
        );
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Visible in App
          <InfoTooltip
            size="sm"
            leftOffset="md"
            iconColor="elm"
            title="The date the shift will be shown and available to claim for this group in the mobile app."
          />
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return (
          <Text>
            <ZonedTime date={record.visibleDate} variant="short_format" />
          </Text>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className="match-breakdown-table"
        columns={columns}
        dataSource={data?.workforceGroupMatchBreakdown}
        pagination={false}
        tableLayout="fixed"
      />

      <style jsx>{`
        :global(.match-breakdown-section) {
          cursor: pointer;
        }
        :global(.match-breakdown-table) {
          margin-bottom: 16px;
          margin-top: 24px;
        }
        :global(.match-breakdown-table .ant-table-row) {
          cursor: default;
        }
        :global(.match-breakdown-table .ant-table-tbody > tr > td) {
          border: none;
          padding: 16px 4px 16px 8px;
        }
        :global(.match-breakdown-table .ant-table-thead > tr > th) {
          border: none;
          background-color: ${Colors.steelLightest};
          padding-top: 10px;
          padding-bottom: 10px;
          font-weight: 400;
        }
        :global(
          .match-breakdown-table .ant-table-tbody > tr.ant-table-row:hover > td
        ) {
          background: none !important;
        }
      `}</style>
    </>
  );
}
